import { Stack } from '@mantine/core'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { useAdminsContext } from '@/contexts/AdminsContext'
import { useAuth } from '@/plugins/auth'
import {
  QUERY_GET_COLLECTIONS,
  QUERY_GET_CREATORS,
  QUERY_GET_ITEMS,
  QUERY_GET_SUGGESTED_COLLECTIONS,
  QUERY_GET_SUGGESTED_CREATORS,
  QUERY_GET_SUGGESTED_ITEMS,
} from '@/schemas/explore.schema'
import { BodyStack } from '@/styled/components'

import { QueryProps } from '../InfiniteScrolling'
import ExploreHeader from './ExploreHeader'
import ExploreTab from './ExploreTab'

interface ExplorePageProps {}

export interface Totals {
  creators?: number
  items?: number
  collections?: number
  published?: number
  owned?: number
  earned?: number
}

export interface Tabs extends QueryProps {
  id: keyof Totals
  title: string
  // => Optional Fields
  tooltip?: string
  filterOptions: string[]
  sortOptions: string[]
  sortDirection?: 'asc' | 'desc'
}

const Component: React.FC<ExplorePageProps> = () => {
  const { t } = useTranslation()
  const { id } = useParams()

  const { address } = useAuth()
  const { admins } = useAdminsContext()

  const isAdmin = useMemo(
    () => admins.map(a => a.toLowerCase()).includes(address?.toLowerCase() || ''),
    [address, admins]
  )

  // Set totals state
  const [triggerRefetch, setTriggerRefetch] = useState(false)
  const [totals, setTotals] = useState<Totals>({ creators: 0, items: 0, collections: 0 })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const tabs: Tabs[] = useMemo(
    () => [
      {
        id: 'creators',
        contentType: 'creators',
        total: totals.creators || 0,
        title: t('components.profile.creators', 'Creators'),
        tooltip: t(
          'components.items.allCreatorsTooltip',
          'Only creators with an Avatar, Display Name, and at least one published item will be displayed.'
        ),
        filterOptions: isAdmin ? ['Creators', 'All Users', 'Flagged'] : ['Creators', 'All Users'],
        sortOptions: ['# published items', 'join date'],
        query: QUERY_GET_CREATORS,
        suggestionQuery: QUERY_GET_SUGGESTED_CREATORS,
        cols: {
          base: 1,
          sm: 2,
          md: 3,
          lg: 4,
        },
      },
      {
        id: 'items',
        contentType: 'items',
        total: totals.items || 0,
        title: t('components.profile.items', 'Items'),
        filterOptions: isAdmin
          ? ['All', 'Unique', 'Fungible', 'Flagged']
          : ['All', 'Unique', 'Fungible'],
        sortOptions: ['# transfers', 'publish date'],
        query: QUERY_GET_ITEMS,
        suggestionQuery: QUERY_GET_SUGGESTED_ITEMS,
      },
      {
        id: 'collections',
        contentType: 'collections',
        total: totals.collections || 0,
        title: t('components.profile.collections', 'Collections'),
        filterOptions: isAdmin
          ? ['All', 'ERC1155', 'ERC721', 'Flagged']
          : ['All', 'ERC1155', 'ERC721'],
        sortOptions: ['# items', 'publish date'],
        query: QUERY_GET_COLLECTIONS,
        suggestionQuery: QUERY_GET_SUGGESTED_COLLECTIONS,
      },
    ],
    [t, totals]
  )

  return (
    <BodyStack style={{ gap: '30px' }}>
      <ExploreHeader
        tabs={tabs.map(elem => ({
          id: elem.id,
          title: elem.title,
        }))}
        currTabId={id || tabs[0].id}
        totals={totals}
        setTotals={setTotals}
        triggerRefetch={triggerRefetch}
      />
      <Stack style={{ minHeight: '80vh' }}>
        {tabs.map(
          tab =>
            id === tab.id && (
              <ExploreTab
                key={tab.id}
                id={tab.id}
                title={tab.title}
                // Specific
                contentType={tab.contentType}
                query={tab.query}
                suggestionQuery={tab.suggestionQuery}
                total={tab.total}
                cols={tab.cols}
                // Optional
                searchEnabled
                tooltip={tab.tooltip}
                filterOptions={tab.filterOptions}
                sortOptions={tab.sortOptions}
                triggerRefetch={() => setTriggerRefetch(prev => !prev)}
                isAdmin={isAdmin}
              />
            )
        )}
      </Stack>
    </BodyStack>
  )
}

Component.displayName = 'ExplorePage'

export { Component }
